<script setup lang="ts">
import { api } from '@/api';
import { JobOccurrenceDto, ReportOptionDto } from '@/api/generator';
import { close } from '@/components/icons/icons.ts';
import Loader from '@/components/Loader/Loader.vue';
import { useAccountStore } from '@/stores/accountStore.ts';
import { useAuthenticationStore } from '@/stores/authenticationStore.ts';
import { mdiPrinter } from '@mdi/js';
import { storeToRefs } from 'pinia';
import { nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import JobOccurrencesReport from './components/JobOccurrenceReport.vue';
import Button from '@/components/buttons/Button.vue';
import { Handler, Previewer } from 'pagedjs';
import '@/styles/print/print-preview.css';
import { buildOdataQuery } from '@/utils/odata';
import { entityNotFoundErrorHandler } from '@/errorHandling';

const accountStore = useAccountStore();
const { currentUser } = storeToRefs(accountStore);

const { session } = storeToRefs(useAuthenticationStore());

const router = useRouter();

const route = useRoute();
const jobData = ref<JobOccurrenceDto | undefined>();
const reportOption = ref<ReportOptionDto | undefined>();
const protocols = ref<string[]>([]);
const isLoading = ref(true);
const isPreviewReady = ref(false);
const previewContainer = ref<HTMLElement>();
const showNonPrintable = ref(true);

onMounted(async () => {
  const { result } = await api.jobOccurrencesApi.getJobOccurrence(
    {
      id: route.params.id as string,
    },
    [entityNotFoundErrorHandler(router)],
  );

  if (result) {
    jobData.value = result;
  }

  // report options (address and logo)
  const { result: reportOptionResult } = await api.reportsApi.getReportOption();

  if (reportOptionResult) {
    reportOption.value = reportOptionResult;
  }

  const workItems = jobData.value?.workItems || [];

  // job data comments
  for (let i = 0; i < workItems.length; i++) {
    const wi = workItems[i];
    const entityId = wi.commentEntityCompositeId;
    if (entityId) {
      const query = buildOdataQuery({
        filter: { entityId },
      });
      const { result: commentsResult } = await api.commentsApi.getPtwCommentList(query);

      if (commentsResult?.value?.length && jobData.value?.workItems?.length) {
        jobData.value.workItems[i].comments = commentsResult.value;
      }
    }
  }

  if (typeof jobData.value?.jobConfigurationId !== 'string') {
    throw new Error('Job configuration id not found');
  }

  protocols.value = jobData.value.protocols?.map((protocol) => protocol.name) ?? [];
  if (jobData.value && protocols.value) {
    isLoading.value = false;

    await nextTick();
    await initializePagedPreview();
  }
});

async function initializePagedPreview() {
  try {
    if (!previewContainer.value) {
      console.error('Preview container ref not available');
      return;
    }

    // Initialize previewer
    const previewer = new Previewer({
      auto: false,
    });

    previewer.registerHandlers(
      class PreviewHandler extends Handler {
        afterPreview() {
          showNonPrintable.value = true;
        }
        // clone table headers for subsequent pages
        // inspired by https://gitlab.coko.foundation/pagedjs/experiments/-/blob/master/tables-SameColsOnDifferentPages/index.html?ref_type=heads
        afterPageLayout(pageElement: HTMLDivElement) {
          const tables = pageElement.querySelectorAll<HTMLTableElement>('table[data-split-from]');

          tables.forEach((table) => {
            const reference = table.dataset.ref;
            const sourceTable = pageElement.previousElementSibling?.querySelector(
              "[data-ref='" + reference + "']",
            );

            // clone Header
            const header = sourceTable?.querySelector('thead');

            if (header) {
              const clonedHeader = header.cloneNode(true);
              // Insert the header at the start of the split table
              table.insertBefore(clonedHeader, table.firstChild);
            }
          });
        }
      },
    );

    // Get the source content
    const sourceContent = document.getElementById('report-source');

    if (sourceContent && previewContainer.value) {
      previewContainer.value.innerHTML = '';

      await previewer.preview(
        sourceContent.innerHTML,
        ['/styles/print/print-report.css'],
        previewContainer.value,
      );

      isPreviewReady.value = true;
    }
  } catch (error) {
    console.error('Failed to initialize paged.js preview:', error);
    isPreviewReady.value = false;
  }
}

function printReport() {
  showNonPrintable.value = false;

  window.print();

  showNonPrintable.value = true;
}

function closePreview() {
  window.close();
}
</script>

<template>
  <Loader v-if="isLoading" type="overlay" :text="$t('LoadingPrintPreview')" />

  <template v-else>
    <div class="print-controls no-print">
      <div class="w-100 text-center">
        <Button
          variant="text"
          :disabled="!isPreviewReady"
          :label="$t('PrintReport')"
          :prependIcon="mdiPrinter"
          @click="printReport"
        />
        <Button
          variant="text"
          class="float-right"
          exact
          :icon="close"
          aria-label="Back"
          @click="closePreview"
        />
      </div>
    </div>

    <div id="report-source" style="display: none">
      <JobOccurrencesReport
        v-if="jobData && currentUser && session?.ipin"
        :id="route.params.id as string"
        :reportOption="reportOption"
        :data="jobData"
        :user="currentUser.fullName"
        :protocols="protocols"
        :ipin="`IPIN ${session.ipin}`"
      />
    </div>

    <div
      id="preview-container"
      ref="previewContainer"
      class="preview-container"
      :class="{ 'preview-ready': isPreviewReady }"
    ></div>
  </template>
</template>

<style scoped>
.print-controls {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;
  border-bottom: 1px solid #ddd;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Print styles */
@media print {
  .no-print {
    display: none !important;
  }

  .preview-wrapper {
    background: white;
    padding: 0;
    margin: 0;
    height: auto;
    overflow-y: visible;
  }
}

/* Screen-only page shadows and spacing */
@media screen {
  :global(.pagedjs_page) {
    margin-bottom: 20px;
  }
}

/* Remove shadows and margins for print */
@media print {
  :global(.pagedjs_page) {
    box-shadow: none !important;
    margin: 0 !important;
    break-after: page;
  }
}
</style>
